/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Col, Empty, Modal, Row, Select, Typography } from 'antd';
import { FullscreenOutlined } from '@ant-design/icons';
import EchartsWrapper from './EchartsWrapper';
import ChartLoader from './ChartLoader';

// Misc imports
import is from '../../utils/is';
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import { CSAT_ANALYTICS_THRESHOLDS } from '../../constants/main';
import useTheme from '../../utils/hooks/useTheme';
import { formatNumberToThousandSeparator } from '../../utils/string/formatNumberToThousandSeparator';

const BAR_CHART_OPTIONS = {
  RESPONSES: {
    label: 'Responses',
    value: 'responses'
  },
  SCORE: {
    label: 'Score',
    value: 'score'
  }
};

const CHART_VIEW = {
  LINE: {
    icon: 'path://M-7.5 -1.036L-5.428 -1.036L-2.714 -7.4562L-0.5545 3.6333L2.2763 -2.1158L3.1518 1.6196L7.5 1.6196M-7.5 7.4562L7.5 7.4562',  // A simple square path icon for testing
    value: 'line'
  },
  BAR: {
    icon: `path://M-6.2277 -1.9018L-3.5491 -1.9018L-3.5491 4.8214L-6.2277 4.8214L-6.2277 -1.9018ZM-1.3527 -4.5536L1.3259 -4.5536L1.3259 4.8214L-1.3527 4.8214L-1.3527 -4.5536ZM3.5491 -7.5L6.2277 -7.5L6.2277 4.8214L3.5491 4.8214L3.5491 -7.5ZM-7.192 7.5L7.192 7.5'`,
    value: 'bar'
  }
};

const ALL_SECTIONS_OPTION = {
  sectionTitle: 'Overall Score (All Sections)',
  sectionId: 'overall_sections_view'
};

const groupDataBySection = (data) => {
  const sectionsMap = new Map();

  sectionsMap.set(ALL_SECTIONS_OPTION.sectionId, {
    sectionTitle: ALL_SECTIONS_OPTION.sectionTitle,
    sectionId: ALL_SECTIONS_OPTION.sectionId,
    values: [...data]
  });

  data?.forEach(record => {
    record.sections?.forEach(section => {
      const { averageScore, averageScorePCT, count, sectionId, sectionTitle } = section;
      if (!sectionsMap.has(sectionId)) {
        sectionsMap.set(sectionId, {
          sectionTitle,
          sectionId,
          values: [
            {
              averageScore,
              averageScorePCT,
              count,
              date: record.date
            }
          ]
        });
      } else {
        const existingSection = sectionsMap.get(sectionId);

        existingSection?.values?.push({
          averageScore,
          averageScorePCT,
          count,
          date: record.date
        });

        sectionsMap.set(sectionId, existingSection);
      }
    });
  });

  return sectionsMap;
};

const CSATOvertimeScoreSection = ({ data = [], sectionTitle = '', seriesName = '', isLoading = true, renderOnlyChart = false, renderOnlyTable = false }) => {
  const [chartToFullscreen, setChartToFullscreen] = useState();
  const { t } = useTranslation();
  const [currentBarChartMode, setCurrentBarChartMode] = useState(BAR_CHART_OPTIONS.SCORE.value);
  const [currentChartView, setCurrentChartView] = useState(CHART_VIEW.BAR.value);
  const [currentSelectedSection, setCurrentSelectedSection] = useState(ALL_SECTIONS_OPTION.sectionId);
  const { successColorAnalytics: successColor, warningColorAnalytics: warningColor, errorColorAnalytics: errorColor, textColor, isMobileOrTabletSize } = useTheme();

  const groupedChartDataBySectionMap = useMemo(() => {
    return groupDataBySection(data);
  }, [data]);

  const getOptions = (data, barChartView = BAR_CHART_OPTIONS.SCORE.value, chartMode = CHART_VIEW.BAR.value) => {
    const dates = data.map(item => item.date);
    const averageScores = data.map(item => item.averageScore);
    const averageScorePCTs = data.map(item => item.averageScorePCT);
    const counts = data.map(item => item.count);
    const maxCount = Math.max(...counts);
    const normallizedCounts = counts.map((count) => (count/maxCount)*100);

    let positiveResponses = [];
    let neutralResponses = [];
    let negativeResponses = [];

    data.forEach((item) => {
      let totalPositiveResponses = 0;
      let totalNeutralResponses = 0;
      let totalNegativeResponses = 0;

      (item.sections || []).forEach((currentValue) => {
        if (currentValue.averageScorePCT >= CSAT_ANALYTICS_THRESHOLDS.POSITIVE.threshold) {
          totalPositiveResponses += currentValue.count;
        } else if (currentValue.averageScorePCT >= CSAT_ANALYTICS_THRESHOLDS.NEUTRAL.threshold) {
          totalNeutralResponses += currentValue.count;
        } else {
          totalNegativeResponses += currentValue.count;
        }
      });
      positiveResponses.push(totalPositiveResponses);
      neutralResponses.push(totalNeutralResponses);
      negativeResponses.push(totalNegativeResponses);
    });

    return {
      tooltip: {
        trigger: 'item',
        axisPointer: {
          type: 'line'
        },
        className: chartMode === CHART_VIEW.LINE.value ? 'ant-tooltip p-0 border-none' : '',
        formatter: (params) => {
          if (params.seriesType === 'line') {

            if(chartMode === CHART_VIEW.LINE.value) {
              const date = dates[params.dataIndex];
              const score = averageScorePCTs[params.dataIndex];
              const ratingScore = averageScores[params.dataIndex];
              const responseCount = counts[params.dataIndex];

              let sentiment = CSAT_ANALYTICS_THRESHOLDS.NEGATIVE.label;
              let colorIndicator = errorColor;
              if (score >= 60) {
                sentiment = CSAT_ANALYTICS_THRESHOLDS.POSITIVE.label;
                colorIndicator = successColor;
              } else if (score >= 40) {
                sentiment = CSAT_ANALYTICS_THRESHOLDS.NEUTRAL.label;
                colorIndicator = warningColor;
              }

              return `
              <div 
              class="d-flex flex-column border-radius-base shadow"
              style="width: 220px; max-width: 220px;"
              >
              <div 
              class="bg-layout text-center p-2 border-radius-top-left-base border-radius-top-right-base fw-600 text-wrap"
              >
                ${date}
              </div>
              <div class="d-flex flex-column p-2 w-100">
              <div class="mb-1">
              <span>Day Score:</span>
                <span class="fw-600">${score}% (${ratingScore}/5)</span>
                </div>
                <div class="d-flex my-1 align-items-center">
                <span class="d-inline-block mr-1" style="width: 8px; height: 8px; border-radius: 50%; background-color: ${colorIndicator};"></span> 
                <span">${sentiment}</span>
                </div>
                <div class="mb-1">Responses: ${formatNumberToThousandSeparator(responseCount)}</div>
              </div>
            </div>`;
            }
            if(currentBarChartMode === BAR_CHART_OPTIONS.SCORE.value) {
              const score = averageScorePCTs[params.dataIndex];
              let markerColor;
              if (score >= CSAT_ANALYTICS_THRESHOLDS.POSITIVE.threshold) {
                markerColor = successColor;
              } else if (score >= CSAT_ANALYTICS_THRESHOLDS.NEUTRAL.threshold) {
                markerColor = warningColor;
              } else {
                markerColor = errorColor;
              }

              const marker = `<span class="d-inline-block mr-1" style="border-radius:10px;width:10px;height:10px;background-color:${markerColor};"></span>`;

              return `${marker} Responses: ${counts[params.dataIndex]}`;
            }

            if (currentBarChartMode === BAR_CHART_OPTIONS.RESPONSES.value) {
              const totalResponses = counts[params.dataIndex];
              const positive = positiveResponses[params.dataIndex];
              const neutral = neutralResponses[params.dataIndex];
              const negative = negativeResponses[params.dataIndex];

              const positiveMarkup = `<div><span class="d-inline-block mr-1" style="border-radius:10px;width:10px;height:10px;background-color:${successColor};"></span> ${CSAT_ANALYTICS_THRESHOLDS.POSITIVE.label}: ${positive}</div>`;
              const neutralMarkup = `<div><span class="d-inline-block mr-1" style="border-radius:10px;width:10px;height:10px;background-color:${warningColor};"></span> ${CSAT_ANALYTICS_THRESHOLDS.NEUTRAL.label}: ${neutral}</div>`;
              const negativeMarkup = `<div><span class="d-inline-block mr-1" style="border-radius:10px;width:10px;height:10px;background-color:${errorColor};"></span> ${CSAT_ANALYTICS_THRESHOLDS.NEGATIVE.label}: ${negative}</div>`;

              return `
                <div>
                  <strong>Total Responses: ${formatNumberToThousandSeparator(totalResponses)}</strong><br>
                  ${positiveMarkup}
                  ${neutralMarkup}
                  ${negativeMarkup}
                </div>
              `;
            }

          }
          return null;
        }
      },
      grid: {
        left: '4%',
        right: '6%',
        bottom: '1%',
        top: '10%',
        containLabel: true
      },
      toolbox: {
        show: true,
        feature: {
          myReplaceWithLine: {
            show: barChartView !== BAR_CHART_OPTIONS.RESPONSES.value,
            title: '',
            icon: CHART_VIEW.LINE.icon,
            iconStyle: {
              borderColor: chartMode === CHART_VIEW.LINE.value ? 'rgb(62, 152, 197)' : textColor
            },
            onclick: () => {
              setCurrentChartView(CHART_VIEW.LINE.value);
            }
          },
          myReplaceWithBar: {
            show: true,
            title: '',
            icon: CHART_VIEW.BAR.icon,
            iconStyle: {
              borderColor: chartMode === CHART_VIEW.BAR.value ? 'rgb(62, 152, 197)' : textColor
            },
            onclick: () => {
              setCurrentChartView(CHART_VIEW.BAR.value);
            }
          }
        }
      },
      xAxis: [{
        type: 'category',
        position: 'bottom',
        axisTick: {
          show: true,
          alignWithLabel: true
        },
        axisLine: {
          show: false
        },
        axisLabel: {
          show: true,
          // How many values will be skipped between labels in the X Axis
          // e.x For 10 dates, this will write the first date, skip the next 8, and write the last
          interval: dates?.length - 2
        },
        data: dates
      }],

      yAxis: [
        {
          type: 'value',
          name: '',
          position: 'left',
          alignTicks: true,
          axisLine: {
            show: true,
            lineStyle: {
              color: textColor
            }
          },
          min: 0,
          max: 100,
          axisLabel: {
            formatter: (chartMode === CHART_VIEW.LINE.value || barChartView === BAR_CHART_OPTIONS.SCORE.value) ? '{value}%' : ''
          }
        }
      ],
      series: chartMode === CHART_VIEW.BAR.value ? ([
        ...(barChartView === BAR_CHART_OPTIONS.RESPONSES.value ? [
          {
            name: 'Negative Responses',
            type: 'bar',
            stack: 'total',
            barGap: '0%',
            data: negativeResponses.map((count) => (count/maxCount)*90),
            itemStyle: { color: errorColor },
            label: {
              show: true,
              position: 'inside',
              formatter: (params) => `${negativeResponses[params.dataIndex] > 0 ? formatNumberToThousandSeparator(negativeResponses[params.dataIndex]) : ''}`
            },
            emphasis: {
              disabled: true
            }
          },
          {
            name: 'Neutral Responses',
            type: 'bar',
            stack: 'total',
            barGap: '0%',
            data: neutralResponses.map((count) => (count/maxCount)*90),
            itemStyle: { color: warningColor },
            label: {
              show: true,
              position: 'inside',
              formatter: (params) => `${neutralResponses[params.dataIndex] > 0 ? formatNumberToThousandSeparator(neutralResponses[params.dataIndex]) : ''}`
            },
            emphasis: {
              disabled: true
            }
          },
          {
            name: 'Positive Responses',
            type: 'bar',
            stack: 'total',
            barGap: '0%',
            data: positiveResponses.map((count) => (count/maxCount)*90),
            itemStyle: { color: successColor },
            label: {
              show: true,
              position: 'inside',
              formatter: (params) => `${positiveResponses[params.dataIndex] > 0 ? formatNumberToThousandSeparator(positiveResponses[params.dataIndex]) : ''}`
            },
            emphasis: {
              disabled: true
            }
          },
          {
            name: '',
            type: 'bar',
            yAxisIndex: 0,
            data: counts.map((count) => (count/maxCount)*90),
            barGap: '-100%',
            label: {
              show: true,
              formatter: (params) => counts[params.dataIndex] > 0 ? `{top|${counts[params.dataIndex]}}` : `{top|}`,
              position: 'top',
              color: textColor,
              fontSize: 12,
              fontWeight: 'bold',
              align: 'center',
              rich: {
                top: {
                  position: 'top',
                  align: 'center',
                  color: textColor,
                  fontWeight: 'bold',
                  fontSize: 12,
                  lineHeight: 20,
                  padding: [4, 0, 0, 0]
                }
              }
            },
            itemStyle: {
              color: (params) => {
                return 'transparent';
              }
            },
            emphasis: {
              disabled: true
            }
          }
        ] : [
          {
            name: '',
            type: 'bar',
            yAxisIndex: 0,
            data: averageScorePCTs,
            barGap: '0%',
            label: {
              show: true,
              formatter: (params) => barChartView === BAR_CHART_OPTIONS.SCORE.value ? params.value > 0 ? `{top|${params.value}%}` : `{top|}` : barChartView === BAR_CHART_OPTIONS.RESPONSES.value && `{top|${formatNumberToThousandSeparator(counts[params.dataIndex])}}`,
              position: 'top',
              color: textColor,
              fontSize: 12,
              fontWeight: 'bold',
              align: 'center',
              rich: {
                top: {
                  position: 'top',
                  align: 'center',
                  color: textColor,
                  fontWeight: 'bold',
                  fontSize: 12,
                  lineHeight: 20,
                  padding: [4, 0, 0, 0]
                }
              }
            },
            itemStyle: {
              color: (params) => {
                if(barChartView !== BAR_CHART_OPTIONS.SCORE.value) {
                  return 'transparent';
                }
                const score = params.value;
                if (score >= CSAT_ANALYTICS_THRESHOLDS.POSITIVE.threshold) {
                  return successColor;
                }
                if (score >= CSAT_ANALYTICS_THRESHOLDS.NEUTRAL.threshold) {
                  return warningColor;
                }
                return errorColor;
              }
            },

            emphasis: {
              disabled: true
            }
          },
          {
            name: '',
            type: 'bar',
            yAxisIndex: 0,
            data: averageScorePCTs,
            barGap: '-100%',
            label: {
              show: barChartView === BAR_CHART_OPTIONS.SCORE.value,
              formatter: (params) => `\n{inside|${averageScores[params.dataIndex]}/5}`,
              position: 'insideTop',
              rich: {
                inside: {
                  align: 'center',
                  fontWeight: 'bold',
                  color: textColor,
                  fontSize: 10,
                  padding: [30, 0, 0, 0]

                }
              }
            },
            itemStyle: {
              color: 'transparent'
            },

            emphasis: {
              disabled: true
            }
          }
        ]),

        {
          name: '',
          yAxisIndex: 0,
          type: 'line',
          smooth: true,

          data: normallizedCounts,
          itemStyle: {
            color: 'rgba(0, 0, 0, 0.3)'
          }
        }
      ]) : [
        {
          type: 'line',
          data: averageScorePCTs,
          lineStyle: {
            color: textColor,
            width: 2
          },
          itemStyle: {
            color: (params) => {
              const score = params.value;
              if(counts[params.dataIndex] === 0) {
                return 'gray';
              }
              if (score >= CSAT_ANALYTICS_THRESHOLDS.POSITIVE.threshold) {
                return successColor;
              }
              if (score >= CSAT_ANALYTICS_THRESHOLDS.NEUTRAL.threshold) {
                return warningColor;
              }
              return errorColor;
            },
            borderColor: textColor,
            borderWidth: 2
          },
          label: {
            show: true,
            align: 'center',
            position: 'inside',
            formatter: (params) => {
              const index = params.dataIndex;
              const value = params.value;
              const prevValue = index > 0 ? averageScorePCTs[index - 1] : null;
              const nextValue = index < averageScorePCTs.length - 1 ? averageScorePCTs[index + 1] : null;
              const positionStyle = (prevValue !== null && prevValue > value) && (nextValue !== null && nextValue > value)
                ? 'bottom'
                : 'top';

              if(value === 0) {
                return '';
              }
              return `{${positionStyle}|${value}%}`;
            },
            color: textColor,
            fontWeight: 'bold',
            rich: {
              top: {
                align: 'center',
                color: textColor,
                fontWeight: 'bold',
                fontSize: 14,
                lineHeight: 20,
                padding: [0, 0, 50, 10]
              },
              bottom: {
                align: 'center',
                color: textColor,
                fontWeight: 'bold',
                fontSize: 14,
                lineHeight: 20,
                padding: [50, 0, 0, 10]
              }
            }
          },
          markPoint: {
            symbol: 'circle',
            symbolSize: 24,
            data: averageScorePCTs.map((value, index) => {
              if(counts[index] === 0) {
                return null;
              }
              return ( {
                value: counts[index],
                xAxis: index,
                yAxis: value,
                itemStyle: {
                  color: '#fff',
                  borderWidth: 2,
                  borderColor: (value >= CSAT_ANALYTICS_THRESHOLDS.POSITIVE.threshold) ? successColor : (value >= CSAT_ANALYTICS_THRESHOLDS.NEUTRAL.threshold) ? warningColor : errorColor,
                  align: 'center'
                },
                label: {
                  show: true,
                  formatter: (params) => params.value === 0 ? '' : `${params.value}`,
                  align: 'center',
                  position: 'inside',
                  color: textColor,
                  fontWeight: 'bold'
                }
              });
            }).filter(point => point !== null)
          }
        }
      ]
    };
  };

  const chart
    = (<Fragment>
      {
        !isEmptyArray(data)
          ? <Row>
            <Col xs={24} className="mb-3">
              <Row className="d-flex align-items-center">
                <Col className="mb-2" xs={isMobileOrTabletSize ? 24 : 12}>
                  {
                    currentSelectedSection !== ALL_SECTIONS_OPTION.sectionId ? (
                      <Typography.Text strong>
                        {t(`Score`)}
                      </Typography.Text>
                    ) : (

                      <Select
                        style={{
                          width: isMobileOrTabletSize ? '100%' : '200px'
                        }}
                        value={currentBarChartMode}
                        onSelect={(value) => {
                          setCurrentBarChartMode((prev) => {
                            if(value === BAR_CHART_OPTIONS.RESPONSES.value) {
                              setCurrentChartView(CHART_VIEW.BAR.value);
                            }
                            return value;
                          });
                        }}
                      >
                        {
                          Object.entries(BAR_CHART_OPTIONS).map(([key, item]) => {
                            return (<Select.Option
                              key={key}
                              value={item.value}
                            >
                              {item.label}
                            </Select.Option>
                            );
                          })
                        }
                      </Select>

                    )
                  }
                </Col>
                <Col className="mb-2" xs={isMobileOrTabletSize ? 24 : 12}>
                  <Select
                    className="float-right"
                    size="middle"
                    onSelect={(value) => {
                      setCurrentSelectedSection(prev => {
                        if(value !== ALL_SECTIONS_OPTION.sectionId) {
                          setCurrentBarChartMode(BAR_CHART_OPTIONS.SCORE.value);
                        }
                        return value;
                      });
                    }}
                    value={currentSelectedSection}
                    style={{
                      width: isMobileOrTabletSize ? '100%' : '200px'
                    }}
                  >
                    {
                      Array.from(groupedChartDataBySectionMap?.values())?.map(section =>(
                        <Select.Option
                          key={section?.sectionId}
                          value={section?.sectionId}
                        >
                          {section?.sectionTitle}
                        </Select.Option>
                      ))
                    }
                  </Select>
                </Col>
              </Row>

            </Col>
            <Col xs={24} className="d-flex justify-content-center align-items-center">
              <EchartsWrapper
                key={`${currentChartView === CHART_VIEW.BAR.value ? `${CHART_VIEW.BAR.value}-${currentBarChartMode}` : `${CHART_VIEW.LINE.value}`}-${currentSelectedSection}`}
                chartOptions={getOptions(groupedChartDataBySectionMap?.get(currentSelectedSection)?.values, currentBarChartMode, currentChartView)}
                externalClassname="d-flex justify-content-center"
                style={{ height: 367, minHeight: '367px' }}
                loading={isLoading}
              />
            </Col>
          </Row>
          : <div className="d-flex content-fill flex-column h-100">
            <div className="mt-5 d-flex align-items-center mb-auto text-center flex-auto align-self-center">
              <Empty
                description={(
                  <Typography.Paragraph>
                    {t('No data available for this chart')}
                  </Typography.Paragraph>
                )}
              />
            </div>
          </div>

      }
    </Fragment>
    );

  return (
    <Fragment>
      {
        renderOnlyChart ? chart
          : <Fragment>
            <div className="mb-6 position-relative">
              <Typography.Title level={4} className="mb-6">
                <Typography.Text className="mr-2">{sectionTitle}</Typography.Text>
              </Typography.Title>

              {isLoading ? <ChartLoader /> : chart}

              <Button
                type="text"
                size="large"
                disabled={isLoading}
                icon={<FullscreenOutlined />}
                className="position-absolute top-0 right-0"
                onClick={() => setChartToFullscreen(chart)}
              />
            </div>

            <Modal
              open={is.truthy(chartToFullscreen)}
              onCancel={() => setChartToFullscreen()}
              destroyOnClose
              footer={null}
              centered
              style={{ maxWidth: 1000 }}
              width={'95%'}
            >
              <div className="p-5">
                {chart}
              </div>
            </Modal>
          </Fragment>

      }
    </Fragment>
  );
};

CSATOvertimeScoreSection.propTypes = {
  data: PropTypes.object,
  sectionTitle: PropTypes.string,
  seriesName: PropTypes.string,
  isLoading: PropTypes.bool,
  renderOnlyChart: PropTypes.bool,
  renderOnlyTable: PropTypes.bool
};

export default CSATOvertimeScoreSection;
